import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { COUNTRIES, codeToFlag } from "../../lib/countries";
import FlagImg from "../ui/FlagImg";

export interface SignupForm {
  email: string;
  straat: string;
  voornaam: string;
  huisnummer: string;
  toevoeging: string;
  naam: string;
  postcode: string;
  wachtwoord: string;
  stad: string;
  land: string;
  iban: string;
  bic: string;
}

interface SignupViewProps {
  form: SignupForm;
  avatarUrl: string | null;
  busy: boolean;
  error: string | null;
  showPwError: boolean;
  pwInvalid: boolean;
  firstInputRef: React.RefObject<HTMLInputElement | null>;
  fileRef: React.RefObject<HTMLInputElement | null>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onPickAvatar: () => void;
  onAvatarSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}

export default function SignupView({
  form,
  busy,
  error,
  showPwError,
  pwInvalid,
  firstInputRef,
  fileRef,
  onChange,
  onAvatarSelected,
  onSubmit,
  onBack,
}: SignupViewProps) {
  const { t, i18n } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);

  // Pick country label in current UI language
  const lang = i18n.language?.split("-")[0] as "nl" | "en" | "fr";
  const countryLabel = (c: typeof COUNTRIES[number]) =>
    lang === "fr" ? c.fr : lang === "en" ? c.en : c.nl;

  const canGoToStep2 =
    form.email.trim() !== "" &&
    form.voornaam.trim() !== "" &&
    form.naam.trim() !== "" &&
    !pwInvalid &&
    form.wachtwoord.trim() !== "";

  const canSubmitStep2 =
    form.straat.trim() !== "" &&
    form.huisnummer.trim() !== "" &&
    form.postcode.trim() !== "" &&
    form.stad.trim() !== "" &&
    form.land.trim() !== "" &&
    form.iban.trim() !== "";

  const handleStep1Next = () => {
    if (canGoToStep2) setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleStep1Next();
    } else {
      onSubmit(e);
    }
  };

  return (
    <div className="reg-card" onClick={(e) => e.stopPropagation()}>
      <div className="reg-layout">
        {/* ---- LEFT SIDEBAR ---- */}
        <div className="reg-sidebar">
          <button
            type="button"
            className="reg-back-btn"
            onClick={step === 2 ? () => setStep(1) : onBack}
            aria-label={t("common.back", "Terug")}
          >
            ←
          </button>

          <div className="reg-sidebar-content">
            <span className="reg-badge">
              {t("auth.freeAccount", "Gratis account")}
            </span>
            <h2 className="reg-sidebar-title">
              {t("auth.createFsmAccount", "Maak je FSM-account aan")}
            </h2>
            <p className="reg-sidebar-subtitle">
              {t(
                "auth.createAccountSubtitle",
                "Bewaar favorieten, breng biedingen uit en verkoop je shirts op één plek.",
              )}
            </p>

            <div className="reg-sidebar-login">
              <p className="reg-sidebar-login-label">
                {t("auth.alreadyHaveAccount", "Heb je al een account?")}
              </p>
              <button
                type="button"
                className="reg-sidebar-login-link"
                onClick={onBack}
              >
                {t("auth.loginHere", "Log hier in")}
              </button>
            </div>
          </div>
        </div>

        {/* ---- RIGHT FORM ---- */}
        <div className="reg-form-side">
          <div className="reg-form-header">
            <div>
              <p className="reg-step-label">
                {step === 1
                  ? t("auth.step1of2", "Stap 1 van 2")
                  : t("auth.step2of2", "Stap 2 van 2")}
              </p>
              <h3 className="reg-form-title">
                {step === 1
                  ? t("auth.yourDetails", "Je gegevens")
                  : t("auth.yourAddress", "Je adres")}
              </h3>
            </div>
            <button
              type="button"
              className="reg-close-btn"
              onClick={onBack}
              aria-label={t("common.close", "Sluiten")}
            >
              ✕
            </button>
          </div>

          {/* Progress bar */}
          <div className="reg-progress">
            <div
              className="reg-progress-fill"
              style={{ width: step === 1 ? "50%" : "100%" }}
            />
          </div>

          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <>
                <div className="reg-fields">
                  <label className="reg-field reg-field--full">
                    <span className="reg-label">
                      {t("auth.email", "E-mail")}
                    </span>
                    <input
                      ref={firstInputRef}
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      placeholder="jij@voorbeeld.com"
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.firstName", "Voornaam")}
                    </span>
                    <input
                      name="voornaam"
                      value={form.voornaam}
                      onChange={onChange}
                      placeholder={t("auth.firstName", "Voornaam")}
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.lastName", "Naam")}
                    </span>
                    <input
                      name="naam"
                      value={form.naam}
                      onChange={onChange}
                      placeholder={t("auth.lastName", "Naam")}
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.password", "Wachtwoord")}
                    </span>
                    <input
                      name="wachtwoord"
                      type="password"
                      required
                      value={form.wachtwoord}
                      onChange={onChange}
                      placeholder={t(
                        "auth.passwordPlaceholder",
                        "Minstens 8 karakters",
                      )}
                      aria-invalid={showPwError}
                      aria-describedby="pw-help"
                      className={showPwError ? "error" : undefined}
                    />
                    <small id="pw-help" className="reg-field-hint">
                      {t(
                        "auth.passwordHint",
                        "Gebruik minstens 8 karakters, 1 cijfer en 1 speciaal teken.",
                      )}
                    </small>
                  </label>
                </div>

                {/* Terms checkbox */}
                <label className="reg-terms">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                  />
                  <span>
                    {t("auth.agreePrefix", "Ik ga akkoord met de")}{" "}
                    <span className="reg-terms-link">
                      {t("auth.terms", "Algemene Voorwaarden")}
                    </span>{" "}
                    {t("auth.legalAnd", "en")}{" "}
                    <span className="reg-terms-link">
                      {t("auth.privacy", "Privacyverklaring")}
                    </span>
                    .
                  </span>
                </label>

                {error && <div className="reg-error">{error}</div>}

                <div className="reg-form-actions">
                  <button
                    type="button"
                    className="reg-back-action"
                    onClick={onBack}
                  >
                    {t("common.back", "Terug")}
                  </button>
                  <button
                    type="button"
                    className="reg-submit-action"
                    disabled={!canGoToStep2 || !agreed}
                    onClick={handleStep1Next}
                  >
                    {t("auth.continue", "Verder")}
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="reg-fields">
                  <label className="reg-field reg-field--full">
                    <span className="reg-label">
                      {t("auth.street", "Straat")}
                    </span>
                    <input
                      name="straat"
                      value={form.straat}
                      onChange={onChange}
                      placeholder={t("auth.streetPlaceholder", "Straatnaam")}
                      required
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.houseNumber", "Huisnummer")}
                    </span>
                    <input
                      name="huisnummer"
                      value={form.huisnummer}
                      onChange={onChange}
                      placeholder="12"
                      required
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.addition", "Toevoeging")}{" "}
                      <span className="reg-label-optional">
                        ({t("common.optional", "optioneel")})
                      </span>
                    </span>
                    <input
                      name="toevoeging"
                      value={form.toevoeging}
                      onChange={onChange}
                      placeholder="A"
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.postalCode", "Postcode")}
                    </span>
                    <input
                      name="postcode"
                      value={form.postcode}
                      onChange={onChange}
                      placeholder="1000"
                      required
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      {t("auth.city", "Stad")}
                    </span>
                    <input
                      name="stad"
                      value={form.stad}
                      onChange={onChange}
                      placeholder={t("auth.cityPlaceholder", "Brussel")}
                      required
                    />
                  </label>

                  <label className="reg-field reg-field--full">
                    <span className="reg-label">
                      {t("auth.country", "Land")}
                    </span>
                    <div className="reg-country-wrap">
                      {form.land && (
                        <span className="reg-country-flag" aria-hidden="true">
                          <FlagImg
                            code={COUNTRIES.find((c) => countryLabel(c) === form.land)?.code ?? ""}
                            size={20}
                          />
                        </span>
                      )}
                      <select
                        name="land"
                        value={form.land}
                        onChange={onChange}
                        className={`reg-country-select${!form.land ? " reg-country-select--placeholder" : ""}`}
                      >
                        <option value="" disabled>
                          {t("auth.countryPlaceholder", "Kies je land...")}
                        </option>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={countryLabel(c)}>
                            {codeToFlag(c.code)} {countryLabel(c)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">IBAN</span>
                    <input
                      name="iban"
                      value={form.iban}
                      onChange={onChange}
                      placeholder="BE68 5390 0754 7034"
                      required
                    />
                  </label>

                  <label className="reg-field">
                    <span className="reg-label">
                      BIC{" "}
                      <span className="reg-label-optional">
                        ({t("common.optional", "optioneel")})
                      </span>
                    </span>
                    <input
                      name="bic"
                      value={form.bic}
                      onChange={onChange}
                      placeholder="BBRUBEBB"
                    />
                  </label>
                </div>

                {/* Avatar (hidden file input) */}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onAvatarSelected}
                />

                {error && <div className="reg-error">{error}</div>}

                <div className="reg-form-actions">
                  <button
                    type="button"
                    className="reg-back-action"
                    onClick={() => setStep(1)}
                  >
                    {t("common.back", "Terug")}
                  </button>
                  <button
                    type="submit"
                    className="reg-submit-action"
                    disabled={busy || !canSubmitStep2}
                  >
                    {busy
                      ? t("common.loading", "Even...")
                      : t("auth.createAccountBtn", "Account aanmaken")}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
